/** Pure helpers for the Trending on TikTok tab: labels, history → chart series, sparkline geometry. */
import type { AudioTrend, TrendHistoryPoint, TrendStatus } from '@/api/audioStudioApi';
import type { PlayableItem } from './AudioPlayer';

/** Period (days) → label; mirrors the service mapping 1→1DAY, 7→7DAY, 30→30DAY, 90→90DAY. */
export const TREND_PERIODS = [
  { days: 1, label: '24 hours' },
  { days: 7, label: '7 days' },
  { days: 30, label: '30 days' },
  { days: 90, label: '90 days' },
] as const;
export type TrendPeriod = (typeof TREND_PERIODS)[number]['days'];

/** One trending song as opened / remixed from the list, with the list it came from (the lookalike endpoint needs region / period). */
export interface TrendFocus {
  trend: AudioTrend;
  region: string;
  period: TrendPeriod;
  /** The trend's cover is missing / TikTok's shared default artwork — show a genre tile instead. */
  placeholderCover?: boolean;
}

/** Only "Rising fast" gets colour; every other status reads as calm, neutral text. */
export const TREND_STATUS_META: Record<TrendStatus, { label: string; className: string }> = {
  rising_fast: { label: 'Rising fast', className: 'bg-[#fff1f3] text-[#c01048]' },
  rising: { label: 'Rising', className: 'bg-[#f2f4f7] text-[#475467]' },
  steady: { label: 'Steady', className: 'bg-[#f2f4f7] text-[#475467]' },
  falling: { label: 'Falling', className: 'bg-[#f2f4f7] text-[#475467]' },
  new: { label: 'New', className: 'bg-[#f2f4f7] text-[#475467]' },
};

/** Badge label for a status; null for missing / unknown values (older payloads). */
export function trendStatusLabel(status: string | null | undefined): string | null {
  return status && status in TREND_STATUS_META ? TREND_STATUS_META[status as TrendStatus].label : null;
}

/** Preset-id hints (e.g. `hip_hop`) as words. */
export function hintLabel(id: string | null | undefined): string | null {
  if (!id) return null;
  const s = id.replace(/_/g, ' ');
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** Words kept upper-case when prettifying TikTok's raw genre labels. */
const GENRE_ACRONYMS = new Set(['R&B', 'EDM', 'DJ', 'UK', 'US', 'LGBTQ']);

function prettyGenreWord(w: string, first: boolean): string {
  const up = w.toUpperCase();
  if (GENRE_ACRONYMS.has(up)) return up;
  const lower = w.toLowerCase();
  return first ? lower.charAt(0).toUpperCase() + lower.slice(1) : lower;
}

/**
 * TikTok's raw genre labels as readable text: `HIP_HOP/RAP` → "Hip hop / rap", `TRAP_RAP` → "Trap rap",
 * `ALTERNATIVE/INDIE` → "Alternative / indie". Already-readable labels ("Hip Hop") are kept; the catch-all
 * `ALL` (and blanks) → null so callers can hide them.
 */
export function formatGenreLabel(raw: string | null | undefined): string | null {
  const s = (raw ?? '').trim();
  if (!s || s.toUpperCase() === 'ALL') return null;
  const isRaw = s.includes('_') || s === s.toUpperCase();
  if (!isRaw) return s.replace(/\s*\/\s*/g, ' / ');
  let first = true;
  return s
    .split('/')
    .map((part) => part.replace(/_/g, ' ').trim().split(/\s+/).filter(Boolean).map((w) => {
      // K_POP / J-POP → "K-pop"
      const out = w.split('-').map((x, i) => prettyGenreWord(x, first && i === 0)).join('-');
      first = false;
      return out;
    }).join(' '))
    .filter(Boolean)
    .join(' / ') || null;
}

/** TikTok genres (prettified, "ALL" dropped, de-duplicated) when given, else the preset genre hint. */
export function trendGenres(t: Pick<AudioTrend, 'genres' | 'genre_hint'>): string[] {
  const out: string[] = [];
  for (const g of t.genres || []) {
    const label = formatGenreLabel(g);
    if (label && !out.some((x) => x.toLowerCase() === label.toLowerCase())) out.push(label);
  }
  if (out.length) return out;
  const hint = hintLabel(t.genre_hint);
  return hint ? [hint] : [];
}

export type RankMove = 'up' | 'down' | 'flat' | 'new';

/**
 * Rank movement for display: null → "New"; |change| > 99 → "99+" (a jump from #5490 to #4 is "▲ 99+").
 * Positive = climbed.
 */
export function formatRankChange(v: number | null | undefined): { move: RankMove; label: string; aria: string } {
  if (v == null || !Number.isFinite(v)) return { move: 'new', label: 'New', aria: 'New entry' };
  if (v === 0) return { move: 'flat', label: '', aria: 'No change' };
  const n = Math.abs(Math.round(v));
  const label = n > 99 ? '99+' : String(n);
  return v > 0 ? { move: 'up', label, aria: `Up ${label}` } : { move: 'down', label, aria: `Down ${label}` };
}

/** 7-day views as the list shows them (older payloads only carry `usage_count`). */
export function trendViews7d(t: Pick<AudioTrend, 'views_7d' | 'usage_count'>): number | null {
  return t.views_7d ?? t.usage_count ?? null;
}

/**
 * Whether TikTok sent any view counts for the song. Many tracks only carry a daily rank — then the
 * views / growth stats are hidden rather than shown as "—".
 */
export function trendHasViews(t: Pick<AudioTrend, 'views_7d' | 'usage_count' | 'views_latest'>): boolean {
  return trendViews7d(t) != null || t.views_latest != null;
}

export interface TrendSeriesPoint {
  date: string;
  /** Short axis label, e.g. "3 Sep". */
  label: string;
  rank: number | null;
  views: number | null;
}

/**
 * A finite number, or null. Numeric strings ("12", " 7 ") count too: some cached / proxied payloads send the daily
 * rank as text, and recharts silently draws nothing for string values on a numeric axis.
 */
function num(v: unknown): number | null {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "2026-09-04" → "4 Sep" (UTC, locale-independent so axis labels stay short everywhere). */
function shortDate(iso: string): string {
  const d = new Date(iso.length === 10 ? `${iso}T00:00:00Z` : iso);
  return Number.isNaN(d.getTime()) ? iso : `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]}`;
}

/** History (oldest → newest) as chart rows: sorted by date, bad numbers → null, last 30 days only. */
export function historySeries(history: TrendHistoryPoint[] | null | undefined): TrendSeriesPoint[] {
  if (!history?.length) return [];
  return history
    .filter((p) => p && typeof p.date === 'string' && p.date)
    .slice()
    .sort((a, b) => a.date.localeCompare(b.date))
    .slice(-30)
    .map((p) => ({ date: p.date, label: shortDate(p.date), rank: num(p.rank), views: num(p.views) }));
}

/** Whether the history has enough points to draw a trend (≥ 2 days with views or rank). */
export function hasTrendHistory(series: TrendSeriesPoint[]): boolean {
  return series.filter((p) => p.views != null || p.rank != null).length >= 2;
}

/**
 * Y domain for the (reversed) rank axis: the ranks actually seen, padded a little so a flat or two-point line is not
 * glued to the chart's edge, never above #1. null when no rank is known.
 */
export function rankDomain(series: TrendSeriesPoint[]): [number, number] | null {
  const ranks = series.map((p) => p.rank).filter((r): r is number => r != null);
  if (!ranks.length) return null;
  const min = Math.min(...ranks);
  const max = Math.max(...ranks);
  const pad = Math.max(1, Math.round((max - min) * 0.08));
  return [Math.max(1, min - pad), max + pad];
}

/**
 * Sparkline geometry for a series of values in a `width`×`height` box (y grows downwards, min at the bottom).
 * Nulls are skipped. Returns null when fewer than two values are known.
 */
export function sparklinePoints(values: (number | null)[], width: number, height: number, pad = 2): { line: string; area: string; last: [number, number] } | null {
  const known = values.map((v, i) => [i, v] as const).filter((e): e is readonly [number, number] => e[1] != null && Number.isFinite(e[1]));
  if (known.length < 2) return null;
  const min = Math.min(...known.map(([, v]) => v));
  const max = Math.max(...known.map(([, v]) => v));
  const span = max - min || 1;
  const stepX = values.length > 1 ? (width - pad * 2) / (values.length - 1) : 0;
  const pts = known.map(([i, v]) => {
    const x = pad + i * stepX;
    const y = max === min ? height / 2 : pad + (1 - (v - min) / span) * (height - pad * 2);
    return [Math.round(x * 10) / 10, Math.round(y * 10) / 10] as [number, number];
  });
  const line = pts.map(([x, y], i) => `${i ? 'L' : 'M'}${x} ${y}`).join(' ');
  const first = pts[0];
  const last = pts[pts.length - 1];
  const area = `${line} L${last[0]} ${height} L${first[0]} ${height} Z`;
  return { line, area, last };
}

/** The shared-player item for a trend's preview (same id in the list and the drawer so play state matches). */
export function trendPlayable(t: AudioTrend): PlayableItem {
  return { id: `trend:${t.trend_id}`, title: t.title, subtitle: t.artist, audioUrl: t.preview_url, coverUrl: t.cover_url };
}

export function formatGrowth(pct: number | null | undefined): string | null {
  if (pct == null || !Number.isFinite(pct)) return null;
  return `${pct >= 0 ? '+' : ''}${Math.round(pct)}%`;
}

/**
 * Values for a card / row sparkline: daily views when TikTok sent at least two, else the daily rank inverted
 * (negated) so a climb still draws upwards. null when there is not enough history to draw.
 */
export function trendSparkline(history: TrendHistoryPoint[] | null | undefined): { kind: 'views' | 'rank'; values: (number | null)[] } | null {
  const series = historySeries(history);
  const views = series.map((p) => p.views);
  if (views.filter((v) => v != null).length >= 2) return { kind: 'views', values: views };
  const ranks = series.map((p) => (p.rank == null ? null : -p.rank));
  if (ranks.filter((v) => v != null).length >= 2) return { kind: 'rank', values: ranks };
  return null;
}

/**
 * TikTok sends the same default (red) artwork for many songs without their own cover. A cover is treated as missing
 * when it is blank or when two or more songs in the current list share it — then a generated genre tile is shown.
 */
export function sharedCoverUrls(items: ReadonlyArray<Pick<AudioTrend, 'cover_url'>>): Set<string> {
  const counts = new Map<string, number>();
  for (const t of items) {
    const u = t.cover_url?.trim();
    if (u) counts.set(u, (counts.get(u) ?? 0) + 1);
  }
  return new Set([...counts].filter(([, n]) => n >= 2).map(([u]) => u));
}

/** Whether a trend should show the generated genre tile instead of its cover. */
export function needsPlaceholderCover(t: Pick<AudioTrend, 'cover_url'>, shared: ReadonlySet<string>): boolean {
  const u = t.cover_url?.trim();
  return !u || shared.has(u);
}

/** "this week" etc. for the market insight line, from the list's period. */
export function periodPhrase(days: number): string {
  if (days <= 1) return 'today';
  if (days <= 7) return 'this week';
  if (days <= 30) return 'this month';
  return `in the last ${days} days`;
}

/**
 * The market insight above the trends grid: the two genres most common among songs that are rising / rising fast
 * (prettified, "ALL" dropped; ties keep list order). Without any rising song, the two most common genres overall.
 * null when no song carries a genre.
 */
export function marketInsight(items: ReadonlyArray<Pick<AudioTrend, 'genres' | 'genre_hint' | 'trend_status'>>): { kind: 'rising' | 'top'; genres: string[] } | null {
  const top = (list: typeof items) => {
    const counts = new Map<string, { label: string; n: number; first: number }>();
    list.forEach((t, i) => {
      for (const g of trendGenres(t)) {
        const k = g.toLowerCase();
        const e = counts.get(k);
        if (e) e.n += 1;
        else counts.set(k, { label: g, n: 1, first: i });
      }
    });
    return [...counts.values()].sort((a, b) => b.n - a.n || a.first - b.first).slice(0, 2).map((e) => e.label);
  };
  const rising = top(items.filter((t) => t.trend_status === 'rising' || t.trend_status === 'rising_fast'));
  if (rising.length) return { kind: 'rising', genres: rising };
  const overall = top(items);
  return overall.length ? { kind: 'top', genres: overall } : null;
}
